import { inject, Injectable } from '@angular/core';
import { isBooleanColumn, isDateColumn, isNumberColumn } from '@y42/column-type';
import { DateTimeService } from '@y42/date-time';
import {
  ColumnTypeEnum,
  OperationModel,
  OperationsFilterModel,
  OperatorEnum,
  OperatorTypeEnum,
  OperatorValueTypeEnum,
} from '@y42/json-schema';
import { createOperationModel } from './operation.model';

@Injectable({
  providedIn: 'root',
})
export class OperationService {
  private readonly dateTimeService = inject(DateTimeService);

  public constructDisplayValue(operations: OperationModel[], type: ColumnTypeEnum) {
    if (this.checkIfContainValue(operations[0])) {
      if (isNumberColumn(type)) {
        return this.getDisplayForNumber(operations);
      }
      if (isDateColumn(type)) {
        return this.getDisplayForDate(operations, type);
      }
      if (isBooleanColumn(type)) {
        return this.getDisplayForBoolean(operations);
      }
      if (type === ColumnTypeEnum.String || type === ColumnTypeEnum.ArrayString) {
        return this.getDisplayForText(operations);
      }
      throw new Error('type of constructorDisplayValue not recognized');
    } else {
      return '-';
    }
  }

  public convertFilterStringToObject(
    strfilters: string,
    fields: Array<{ field_name: string; data_type: ColumnTypeEnum }>,
  ): OperationsFilterModel[] {
    const filters: OperationsFilterModel[] = [];
    const strFilterObject = JSON.parse(
      `{"${decodeURI(strfilters)
        .replace(/"/g, '\\"')
        .replace(/&/g, '","')
        .replace(/=/g, '":"')
        .replace(/\\/g, '\\\\')}"}`,
    );
    Object.keys(strFilterObject).forEach((element) => {
      const fieldName = element.substring(element.lastIndexOf('[') + 1, element.lastIndexOf(']'));
      const filterStr = strFilterObject[element];
      const dateFilterType = fieldName.split('|').length > 1 ? fieldName.split('|')[1] : '';
      const filterField = fields.find((f) => f.field_name === fieldName.split('|')[0]);
      if (filterField) {
        const filterOperations = this.convertUrlFilterIntoOperationModel(
          filterStr,
          filterField.data_type,
          dateFilterType,
        );
        const filterModel = {
          variable_field_name: filterField.field_name,
          variable_type: filterField.data_type,
          operations: filterOperations,
        } as OperationsFilterModel;
        filters.push(filterModel);
      }
    });
    return filters;
  }

  public convertUrlFilterIntoOperationModel(
    url = '',
    type: ColumnTypeEnum = ColumnTypeEnum.String,
    dateFilterType = '',
  ): OperationModel[] {
    const filter: string[] = url.split('{-%26-}');
    const operationModels: OperationModel[] = [];
    const value: unknown[] = [];
    filter.forEach((str) => {
      const operation = createOperationModel(type);
      if (/^{-!-}{-%-}/.test(str) && /{-%-}$/.test(str)) {
        operation.operator = OperatorEnum.NotContains;
        str = str.split('{-%-}').join('');
        operation.values[0] = str.replace('{-!-}', '');
      } else if (/^{-%-}/.test(str) && /{-%-}$/.test(str)) {
        operation.operator = OperatorEnum.Contains;
        operation.values[0] = str.split('{-%-}').join('');
      } else if (/^{-%-}/.test(str)) {
        operation.operator = OperatorEnum.EndsWith;
        operation.values[0] = str.replace('{-%-}', '');
      } else if (/{-%-}$/.test(str)) {
        operation.operator = OperatorEnum.StartsWith;
        operation.values[0] = str.replace('{-%-}', '');
      } else if (/^{-!-}{-null-}/.test(str)) {
        operation.values[0] = str.replace('{-!-}{-null-}', '');
        operation.operator = OperatorEnum.IsNotEmpty;
      } else if (/^{-null-}/.test(str)) {
        operation.values[0] = str.replace('{-null-}', '');
        operation.operator = OperatorEnum.IsEmpty;
      } else if (/^{-!-}/.test(str)) {
        operation.values[0] = str.replace('{-!-}', '');
        operation.operator = OperatorEnum.IsNot;
      } else if (/^</.test(str)) {
        operation.operator = OperatorEnum.LessThan;
        operation.values[0] = str.replace('<', '');
      } else if (/^>/.test(str)) {
        operation.operator = OperatorEnum.GreaterThan;
        operation.values[0] = str.replace('>', '');
      } else if (/:/.test(str)) {
        operation.values[0] = str.split(':')[0];
        operation.values[1] = str.split(':')[1];
        operation.operator = OperatorEnum.Between;
      } else {
        value.push(str);
      }

      if (dateFilterType.includes('r')) {
        operation.operator_type = OperatorTypeEnum.Relative;
        if (dateFilterType.includes('rd')) {
          operation.value_type = OperatorValueTypeEnum.Days;
        } else if (dateFilterType.includes('ry')) {
          operation.value_type = OperatorValueTypeEnum.Years;
        } else if (dateFilterType.includes('rm')) {
          operation.value_type = OperatorValueTypeEnum.Months;
        } else if (dateFilterType.includes('rw')) {
          operation.value_type = OperatorValueTypeEnum.Weeks;
        } else if (dateFilterType.includes('rh')) {
          operation.value_type = OperatorValueTypeEnum.Hours;
        }
      } else {
        operation.operator_type = OperatorTypeEnum.Absolute;
      }
      if (operation.operator !== OperatorEnum.Equals) {
        operationModels.push(operation);
      }
    });
    if (value.length > 0) {
      const operationEqual: OperationModel = createOperationModel(type);
      if (value.length > 1) {
        operationEqual.operator = OperatorEnum.In;
      }
      if (type === ColumnTypeEnum.Boolean) {
        value[0] = value[0] === 'true';
      }
      operationEqual.values = value as OperationModel['values'];
      operationModels.push(operationEqual);
    }
    return operationModels;
  }

  private helperDisplayBoolean(value: string) {
    if (value === '1') {
      return 'true';
    }

    if (value === '0') {
      return 'false';
    }
    return value;
  }

  private checkIfContainValue(operation: OperationModel | undefined) {
    // check if there is at least one value in the filter
    if (!operation) {
      return false;
    }

    if (operation?.operator === OperatorEnum.IsEmpty || operation?.operator === OperatorEnum.IsNotEmpty) {
      return true;
    } else {
      return operation?.values.length > 0;
    }
  }

  private getDisplayForNumber(operations: OperationModel[]) {
    let queryString = '';
    operations.forEach((operation, index) => {
      if (operation.operator === OperatorEnum.Between) {
        queryString = queryString + operation.values[0] + operation.operator + operation.values[1];
      } else if (operation.operator === OperatorEnum.Equals || operation.operator === OperatorEnum.In) {
        queryString = queryString + operation.values.join(', ');
      } else if (operation.operator === OperatorEnum.IsNot || operation.operator === OperatorEnum.NotIn) {
        queryString = `${queryString}!${operation.values.join(', !')}`;
      } else if (operation.operator === OperatorEnum.IsEmpty) {
        queryString = `${queryString}empty `;
      } else if (operation.operator === OperatorEnum.IsNotEmpty) {
        queryString = `${queryString}!empty `;
      } else {
        queryString = queryString + operation.operator + operation.values[0];
      }
      if (operations.length > 1 && index + 1 !== operations.length && this.checkIfContainValue(operation)) {
        queryString = `${queryString}, `;
      }
    });
    return queryString;
  }

  private getDisplayForBoolean(operations: OperationModel[]) {
    let queryString = '';
    operations.forEach((operation, index) => {
      if (operation.operator === OperatorEnum.Equals || operation.operator === OperatorEnum.IsNot) {
        queryString = queryString + this.helperDisplayBoolean(operation.values[0] as string);
      }
      if (operations.length > 1 && index + 1 !== operations.length && operation.values[0]) {
        queryString = `${queryString}, `;
      }
    });
    return queryString;
  }

  private parseDateConsideringUserTimezone(dateString: string): Date {
    return this.dateTimeService.add(dateString, {
      minutes: this.dateTimeService.userToBrowserTimeZoneDiff.minutesDiff,
    });
  }

  private getDisplayForDate(operations: OperationModel[], type: ColumnTypeEnum) {
    const format =
      type === ColumnTypeEnum.Timestamp || type === ColumnTypeEnum.Datetime ? 'MMM do yy HH:mm' : 'MMM do yy';
    let queryString = '';
    operations.forEach((operation, index) => {
      const value =
        operation.operator_type === OperatorTypeEnum.Relative
          ? operation.values[0]
          : operation.values[0]
          ? this.dateTimeService.__format(this.parseDateConsideringUserTimezone(operation.values[0] as string), format)
          : '';
      const value_between =
        operation.operator_type === OperatorTypeEnum.Relative
          ? operation.values[1]
          : operation.values[1]
          ? this.dateTimeService.__format(this.parseDateConsideringUserTimezone(operation.values[1] as string), format)
          : '';
      if (operation.operator === OperatorEnum.Between) {
        queryString = `${queryString + value} ${operation.operator} ${value_between}`;
      } else if (operation.operator === OperatorEnum.IsEmpty) {
        queryString = `${queryString}empty `;
      } else if (operation.operator === OperatorEnum.IsNotEmpty) {
        queryString = `${queryString}!empty `;
      } else {
        queryString =
          queryString +
          (operation.operator_type === OperatorTypeEnum.Relative ? 'relative date' : '') +
          operation.operator +
          value;
      }
      if (operations.length > 1 && index + 1 !== operations.length && operation.values[0]) {
        queryString = `${queryString}, `;
      }
    });
    return queryString;
  }

  private getDisplayForText(operations: OperationModel[]) {
    let queryString = '';
    operations.forEach((operation, index) => {
      if (operation.operator === OperatorEnum.Equals || operation.operator === OperatorEnum.In) {
        let textValue = '';
        if (!textValue) {
          textValue = operation.values.join(', ');
        }
        queryString = queryString + textValue;
      } else if (operation.operator === OperatorEnum.IsNot || operation.operator === OperatorEnum.NotIn) {
        queryString = `${queryString}!${operation.values.join(', !')}`;
      } else if (operation.operator === OperatorEnum.EndsWith) {
        queryString = `${queryString}ends with - ${operation.values[0]}`;
      } else if (operation.operator === OperatorEnum.StartsWith) {
        queryString = `${queryString}starts with - ${operation.values[0]}`;
      } else if (operation.operator === OperatorEnum.Contains) {
        queryString = `${queryString}contains - ${operation.values[0]}`;
      } else if (operation.operator === OperatorEnum.NotContains) {
        queryString = `${queryString}not contains - ${operation.values[0]}`;
      } else if (operation.operator === OperatorEnum.IsEmpty) {
        queryString = `${queryString}empty `;
      } else if (operation.operator === OperatorEnum.IsNotEmpty) {
        queryString = `${queryString}!empty `;
      }
      if (operations.length > 1 && index + 1 !== operations.length && this.checkIfContainValue(operation)) {
        queryString = `${queryString}, `;
      }
    });
    return queryString;
  }
}
